import {Component, OnInit} from '@angular/core';
import {SharedService} from '../../shared/services/shared.service';
import {AppState} from '../../store/app.reducer';
import {TitleService} from '../../shared/services/title.service';
import {Store} from '@ngrx/store';
import {BaseComponent} from '../../shared/components/base/base.component';
import {NotificationSettings, UserSettings, ViewMode} from '../../shared/models/userSettings.interface';
import {User} from '../../shared/models/user.interface';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {LanguageService} from '../../shared/services/language.service';
import {TimeZoneService} from '../../shared/services/time-zone.service';
import {resetUpdateUserState, updateUserMerge} from '../../auth/store/auth.actions';
import {take} from 'rxjs/operators';


@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css'],
})
export class SettingsComponent extends BaseComponent implements OnInit {

  emailNotifications: NotificationSettings = {
    bookingAccepted: true,
    bookingCancelled: true,
    bookingCreated: true,
    bookingDenied: true,
    bookingPaid: true,
    mangoPayDocumentTypeAddress: true,
    mangoPayDocumentTypeIdentity: true,
    markedAsPickedUp: true,
    markedAsReturned: true,
    revertedPickedUp: true,
    message: true,
    newBookingDateSuggested: true,
    newBookingDateSuggestionAccepted: true,
    newBookingDateSuggestionDenied: true,
    newBookingDateSuggestionRetracted: true,
    paymentTransferred: true,
    paymentRefunded: true,
    pickupConfirmed: true,
    pickupReminder: true,
    returnConfirmed: true,
    returnReminder: true,
    revertedReturned: true,
  };

  listingsViewMode: ViewMode = 'grid';
  langCode?: string;
  timeZone?: string;
  allNotificationsChecked: boolean = true;
  someNotificationsChecked: boolean = false;


  constructor(protected store: Store<AppState>,
              public languageService: LanguageService,
              private sharedService: SharedService,
              private titleService: TitleService,
              private timeZoneService: TimeZoneService,
  ) {
    super(store);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.titleService.setTitle($localize`Settings`);

    this.user$.subscribe(user => {
      if (!user)
        return;
      this.onUserLoaded(user);
    });

  }

  onSaveNotificationSettings(): void {
    this.clearAlerts();

    if (!this.user) {
      this.addError($localize`You are not logged in.`);
      return;
    }
    this.enableLoadingSpinner($localize`Saving email notification settings...`);

    const settings: UserSettings = this.user?.settings ? {...this.user.settings} : {};
    settings.emailNotifications = this.emailNotifications;
    this.saveSettings(settings);
  }

  onSaveWebsiteSettings(): void {
    this.clearAlerts();

    if (!this.user) {
      this.addError($localize`You are not logged in.`);
      return;
    }
    if (!this.langCode) {
      this.addError($localize`Please select a language.`);
      return;
    }
    if (!this.timeZone) {
      this.addError($localize`Please select a time zone.`);
      return;
    }
    this.enableLoadingSpinner($localize`Saving website settings...`);
    const settings: UserSettings = this.user?.settings ? {...this.user.settings} : {};
    settings.listingsViewMode = this.listingsViewMode;
    settings.lang = this.langCode;
    settings.timeZone = this.timeZone;
    this.saveSettings(settings);
  }

  onTabSwitch($event: MatTabChangeEvent) {
    this.clearAlerts();
  }

  /**
   * Enables or disables all notification sliders
   * @param checked true, to enable them all. false, to disable them all.
   */
  toggleAllNotifications(checked: boolean) {
    this.emailNotifications.bookingAccepted = checked;
    this.emailNotifications.bookingCancelled = checked;
    this.emailNotifications.bookingCreated = checked;
    this.emailNotifications.bookingDenied = checked;
    this.emailNotifications.bookingPaid = checked;
    this.emailNotifications.mangoPayDocumentTypeAddress = checked;
    this.emailNotifications.mangoPayDocumentTypeIdentity = checked;
    this.emailNotifications.markedAsPickedUp = checked;
    this.emailNotifications.markedAsReturned = checked;
    this.emailNotifications.revertedPickedUp = checked;
    this.emailNotifications.message = checked;
    this.emailNotifications.newBookingDateSuggested = checked;
    this.emailNotifications.newBookingDateSuggestionAccepted = checked;
    this.emailNotifications.newBookingDateSuggestionDenied = checked;
    this.emailNotifications.newBookingDateSuggestionRetracted = checked;
    this.emailNotifications.paymentTransferred = checked;
    this.emailNotifications.paymentRefunded = checked;
    this.emailNotifications.pickupConfirmed = checked;
    this.emailNotifications.pickupReminder = checked;
    this.emailNotifications.returnConfirmed = checked;
    this.emailNotifications.returnReminder = checked;
    this.emailNotifications.revertedReturned = checked;
  }

  updateNotificationsMetaCheckbox(): void {
    const checkboxesCount = [...Object.values(this.emailNotifications)].length;
    const checkedCheckboxesCount = [...Object.values(this.emailNotifications)].filter(notificationValue => notificationValue === true).length;
    this.allNotificationsChecked = checkboxesCount === checkedCheckboxesCount;
    this.someNotificationsChecked = checkedCheckboxesCount > 0 && !this.allNotificationsChecked;
    console.log(`checkboxesCount: ${checkboxesCount}, checkedCheckboxesCount: ${checkedCheckboxesCount}, this.allNotificationsChecked: ${this.allNotificationsChecked}, this.someNotificationsChecked: ${this.someNotificationsChecked}`, this.emailNotifications);

  }

  public getTimeZones() {
    return this.timeZoneService.getOrderedTimeZones();
  }

  /**
   * Saves the given settings into this.user and updates the corresponding object in the firestore. this.user must be set.
   * @param settings settings to be written
   */
  private saveSettings(settings: UserSettings) {
    const userUpdate = {uid: this.user!.uid, settings};
    this.store.dispatch(updateUserMerge({userUpdate}));

    this.updateUserActionResult$.pipe(take(1)).subscribe(result => {
      this.disableLoadingSpinner();
      this.store.dispatch(resetUpdateUserState());
      if (result.success) {
        this.emailNotifications = {...this.user?.settings?.emailNotifications};
        this.addSuccess($localize`Saving successful.`);
        // Notify about a possibly changed language. (If it has not been changed, nothing will happen)
        if (this.user?.settings?.lang)
          this.languageService.onLanguageChanged.next(this.user.settings.lang);
      }
      if (result.errorMessage)
        this.addError($localize`Error saving settings.` + result.errorMessage);
    });

  }

  private onUserLoaded(user: User) {
    // Website settings
    this.langCode = user.settings?.lang;
    this.timeZone = user.settings?.timeZone;
    if (user.settings?.listingsViewMode !== undefined)
      this.listingsViewMode = user.settings.listingsViewMode;

    // Email notification settings
    if (user.settings?.emailNotifications?.bookingAccepted !== undefined)
      this.emailNotifications.bookingAccepted = user.settings.emailNotifications.bookingAccepted;
    if (user.settings?.emailNotifications?.bookingCancelled !== undefined)
      this.emailNotifications.bookingCancelled = user.settings.emailNotifications.bookingCancelled;
    if (user.settings?.emailNotifications?.bookingCreated !== undefined)
      this.emailNotifications.bookingCreated = user.settings.emailNotifications.bookingCreated;
    if (user.settings?.emailNotifications?.bookingDenied !== undefined)
      this.emailNotifications.bookingDenied = user.settings.emailNotifications.bookingDenied;
    if (user.settings?.emailNotifications?.bookingPaid !== undefined)
      this.emailNotifications.bookingPaid = user.settings.emailNotifications.bookingPaid;
    if (user.settings?.emailNotifications?.mangoPayDocumentTypeAddress !== undefined)
      this.emailNotifications.mangoPayDocumentTypeAddress = user.settings.emailNotifications.mangoPayDocumentTypeAddress;
    if (user.settings?.emailNotifications?.mangoPayDocumentTypeIdentity !== undefined)
      this.emailNotifications.mangoPayDocumentTypeIdentity = user.settings.emailNotifications.mangoPayDocumentTypeIdentity;
    if (user.settings?.emailNotifications?.markedAsPickedUp !== undefined)
      this.emailNotifications.markedAsPickedUp = user.settings.emailNotifications.markedAsPickedUp;
    if (user.settings?.emailNotifications?.markedAsReturned !== undefined)
      this.emailNotifications.markedAsReturned = user.settings.emailNotifications.markedAsReturned;
    if (user.settings?.emailNotifications?.revertedPickedUp !== undefined)
      this.emailNotifications.revertedPickedUp = user.settings.emailNotifications.revertedPickedUp;
    if (user.settings?.emailNotifications?.message !== undefined)
      this.emailNotifications.message = user.settings.emailNotifications.message;
    if (user.settings?.emailNotifications?.newBookingDateSuggested !== undefined)
      this.emailNotifications.newBookingDateSuggested = user.settings.emailNotifications.newBookingDateSuggested;
    if (user.settings?.emailNotifications?.newBookingDateSuggestionAccepted !== undefined)
      this.emailNotifications.newBookingDateSuggestionAccepted = user.settings.emailNotifications.newBookingDateSuggestionAccepted;
    if (user.settings?.emailNotifications?.newBookingDateSuggestionDenied !== undefined)
      this.emailNotifications.newBookingDateSuggestionDenied = user.settings.emailNotifications.newBookingDateSuggestionDenied;
    if (user.settings?.emailNotifications?.newBookingDateSuggestionRetracted !== undefined)
      this.emailNotifications.newBookingDateSuggestionRetracted = user.settings.emailNotifications.newBookingDateSuggestionRetracted;
    if (user.settings?.emailNotifications?.paymentTransferred !== undefined)
      this.emailNotifications.paymentTransferred = user.settings.emailNotifications.paymentTransferred;
    if (user.settings?.emailNotifications?.paymentRefunded !== undefined)
      this.emailNotifications.paymentRefunded = user.settings.emailNotifications.paymentRefunded;
    if (user.settings?.emailNotifications?.pickupConfirmed !== undefined)
      this.emailNotifications.pickupConfirmed = user.settings.emailNotifications.pickupConfirmed;
    if (user.settings?.emailNotifications?.pickupReminder !== undefined)
      this.emailNotifications.pickupReminder = user.settings.emailNotifications.pickupReminder;
    if (user.settings?.emailNotifications?.returnConfirmed !== undefined)
      this.emailNotifications.returnConfirmed = user.settings.emailNotifications.returnConfirmed;
    if (user.settings?.emailNotifications?.returnReminder !== undefined)
      this.emailNotifications.returnReminder = user.settings.emailNotifications.returnReminder;
    if (user.settings?.emailNotifications?.revertedReturned !== undefined)
      this.emailNotifications.revertedReturned = user.settings.emailNotifications.revertedReturned;
    this.updateNotificationsMetaCheckbox();
  }
}
